<template>
  <div class="editUsersSetting">
    <setting-header />
    <v-divider class="editUsersSetting_divider" />
    <!-- New user display -->
    <template v-if="isNew">
      <edit-users :submit-loading="submitLoading" @onSubmit="handleCreate" />
      <clip-dialog-message
        :open="!!dialogMessageText"
        :message="dialogMessageText"
        @onClose="closeDialogMessage(true)"
      />
    </template>
    <!-- End new user display -->

    <!-- User display -->
    <template v-else>
      <section class="editUsersSetting_main">
        <div v-ripple class="editUsersSetting_item">
          <h3>
            {{ name }}
            <v-icon class="editUsersSetting_item--icon" size="22">
              mdi-chevron-right
            </v-icon>
          </h3>

          <span class="editUsersSetting_item-desc">{{ email }}</span>
        </div>
        <v-divider class="editUsersSetting_main--divider" />
        <v-skeleton-loader
          v-if="initLoading"
          :loading="initLoading"
          type="sentences, table-heading"
          class="editUsersSetting_main--initLoading"
        ></v-skeleton-loader>
        <section
          v-else
          v-ripple
          class="editUsersSetting_collapse"
          @click="handleSelectCam"
        >
          <h3>閲覧できるカメラグループ</h3>
          <v-icon class="editUsersSetting_collapse--icon" size="22">
            mdi-chevron-right
          </v-icon>
          <h6>閲覧できるカメラグループを選択してください</h6>
          <div v-if="!initLoading" class="editUsersSetting_selectedCams">
            <div
              v-for="(cam, index) in selectedCams"
              :key="index"
              class="editUsersSetting_selectedCams--items"
            >
              <tooltip :classes="'settingHeader_breadcrumbs-item-tooltip'">
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    class="editUsersSetting_selectedCams--title text-truncate"
                    v-on="on"
                  >
                    {{ cam.title }}
                  </div>
                </template>
                <h4>{{ cam.title }}</h4>
              </tooltip>
              <span v-if="cam.cameraPolicyAccess">カメラの操作可能</span>
              <span v-else-if="cam.cameraPolicyLiveOnly">
                LIVE映像のみ閲覧可能
              </span>
            </div>
          </div>
        </section>
        <v-divider class="editUsersSetting_main--divider" />
        <div class="editUsersSetting_action editUsersSetting_action-delete">
          <h3>このユーザーを削除する</h3>
          <v-btn
            text
            icon
            class="editUsersSetting_btn-delete"
            @click="handleDelete"
          >
            <span class="icon icon-trash" />
          </v-btn>
        </div>
        <!-- Dialogs -->
        <template>
          <!-- Delete dialog -->
          <clip-dialog-a
            :open="dialogDeleteOpen"
            title="ユーザー情報の削除"
            @onClose="closeDialogDelete"
          >
            <div class="editUsersSetting_dialog-content">
              このユーザー情報を<br />
              完全に削除してもよろしいですか？
            </div>
            <template v-slot:actions>
              <v-btn
                class="
                  editUsersSetting_dialog-btn editUsersSetting_dialog-cancel
                "
                color="text"
                @click="closeDialogDelete"
              >
                キャンセル
              </v-btn>
              <v-spacer />
              <v-btn
                class="
                  editUsersSetting_dialog-btn editUsersSetting_dialog-delete
                "
                color="text"
                :loading="deleteLoading"
                @click="handleDeleteOk"
              >
                削除する
              </v-btn>
            </template>
          </clip-dialog-a>
          <!-- ----------------- -->
          <!-- Message dialog -->
          <clip-dialog-message
            :open="!!dialogMessageText"
            :message="dialogMessageText"
            @onClose="closeDialogMessage"
          />
          <!-- ----------------- -->
        </template>
      </section>
    </template>
    <!-- End user disply -->
  </div>
</template>

<script>
import ClipDialogA from '@/components/dialogs/ClipDialogA.vue';
import ClipDialogMessage from '@/components/dialogs/ClipDialogMessage.vue';
import SettingHeader from '../SettingHeader.vue';
import { mapActions, mapState } from 'vuex';
import EditUsers from './EditUsers.vue';

export default {
  name: 'EditUsersSetting',
  components: {
    SettingHeader,
    ClipDialogA,
    ClipDialogMessage,
    EditUsers,
  },
  data() {
    return {
      initLoading: false,
      dialogDeleteOpen: false,
      dialogMessageText: '',
      name: '',
      email: '',
      submitLoading: false,
      deleteLoading: false,
      liveModeSwitch: false,
    };
  },
  computed: {
    ...mapState({
      users: (state) => state.settings.users,
      currentAvatarId: (state) => state.settings.currentAvatarId,
      currentCameraDirectoryId: (state) =>
        state.settings.currentCameraDirectoryId,
      selectedCams: (state) => state.settings.newUserCamList,
      groupCams: (state) => state.settings.groupCams,
    }),
    userId() {
      return this.$route.params.userId;
    },
    user() {
      return this.users.find(
        (user) => String(user.id) === String(this.userId) && !user.owner
      );
    },
    isEdit() {
      return this.$route.name === 'settings-users-edit';
    },
    isNew() {
      return !this.isEdit;
    },
  },
  watch: {
    dialogDeleteOpen(val) {
      val || this.closeDialogDelete();
    },
    dialogMessageText(val) {
      val || this.closeDialogMessage();
    },
    user(val) {
      if (val) {
        this.name = val.name;
        this.email = val.email;
      }
    },
    users(val) {
      if (val && !this.user && !this.submitLoading && !this.isNew) {
        this.$router.push({ name: 'settings-users-main' });
      }
    },
  },
  mounted() {
    this.init();
    if (this.user) {
      this.name = this.user.name;
      this.email = this.user.email;
    }
    if (!this.isEdit) {
      this.email = this.$route.query.email ? this.$route.query.email : '';
      this.liveModeSwitch = this.$route.query.switch
        ? this.$route.query.switch
        : '';
    }
  },
  methods: {
    ...mapActions('settings', [
      'registerInvitedUser',
      'setNewUserCamList',
      'removeAvatar',
      'fetchCameraGroup',
      'fetchUserAvatarList',
      'editUserCameraGroup',
    ]),
    async init() {
      this.initLoading = true;
      await this.fetchCameraGroup({ shareScope: 1 });
      // only call `editUserCameraGroup` when in edit mode and user id exists
      if (this.isEdit) {
        if (!this.userId) {
          console.error('User ID is not available');
        }
        await this.editUserCameraGroup({ avatarIds: [this.userId] });
      }
      this.initLoading = false;
    },
    handleSelectCam() {
      if (this.isEdit) {
        this.routerPush({
          name: 'settings-users-edit-select-cam',
          params: this.$route.params,
        });
      } else {
        this.routerPush({
          name: 'settings-users-select-cam',
          query: this.$route.query,
        });
      }
    },
    handleSwitch(status) {
      if (!this.isEdit) {
        this.routerPush({
          path: this.$route.path,
          query: { ...this.$route.query, switch: status },
        });
      }
    },
    handleEmailChange(email) {
      if (!this.isEdit) {
        this.routerPush({
          path: this.$route.path,
          query: { ...this.$route.query, email },
        });
      }
    },
    async handleCreate({ email, authList }) {
      try {
        this.submitLoading = true;
        await this.registerInvitedUser({
          mail_address: email,
          avatar_id: Number(this.currentAvatarId),
          camera_directory_id: Number(this.currentCameraDirectoryId),
          auth_list: authList,
        });
        await this.fetchUserAvatarList();
        this.dialogMessageText = '招待メールを送信しました。';
      } catch (error) {
        console.error(error);
      } finally {
        this.submitLoading = false;
      }
    },
    handleDelete() {
      this.dialogDeleteOpen = true;
    },
    async handleDeleteOk() {
      try {
        this.deleteLoading = true;
        await Promise.all(
          this.user.avatarIds.map((id) => {
            return this.removeAvatar(id);
          })
        );
        this.closeDialogDelete();
        this.$router.push({ name: 'settings-users-main', query: { dl: 1 } }); // set delete flag to true in query string to display deleted message in list page
      } catch (error) {
        console.error(error);
      } finally {
        this.deleteLoading = false;
      }
    },
    closeDialogDelete() {
      this.dialogDeleteOpen = false;
    },
    closeDialogMessage(closeAndMove = false) {
      this.dialogMessageText = null;
      closeAndMove && this.$router.push({ name: 'settings-users-main' });
    },
  },
};
</script>

<style lang="scss">
.editUsersSetting {
  &_main {
    height: calc(100vh - 200px);
    overflow-y: auto;
    @include scrollBarCustom;
    &--divider {
      border-color: #e6e6e6;
    }
    &--initLoading {
      margin: 15px 50% 15px 0;
    }
  }
  &_divider {
    margin-top: 15px;
    &.theme--light.v-divider {
      border-color: #ccc;
    }
  }
  &_gap-header {
    height: 26px;
  }
  &_item {
    margin: 15px 0;
    cursor: pointer;
    h3 {
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }
    &-desc {
      font-size: 12px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: left;
      color: #989898;
    }
    &--icon {
      color: #c1c1c1 !important;
    }
  }
  &_name {
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    text-align: left;
    color: #666;
    margin-top: 10px;
    margin-bottom: 10px;
    height: 20px;
    display: block;
  }
  &_note {
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    text-align: right;
    color: #eb545b;
  }
  &_input {
    &-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 500px;
    }
    &-name {
      max-width: 500px;
      &.theme--light.v-input {
        input {
          font-size: 16px;
          font-weight: normal;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.38;
          letter-spacing: normal;
          text-align: left;
          color: #4b4b4b;
        }
        .v-text-field__details {
          padding: 0 8px 0 0;
          .v-messages.theme--light:not(.error--text) {
            .v-messages__message {
              font-size: 12px;
              font-weight: normal;
              font-stretch: normal;
              font-style: normal;
              letter-spacing: normal;
              text-align: left;
              color: #989898;
            }
          }
        }
      }
    }
    &-email {
      &.theme--light.v-input {
        .v-text-field__details {
          padding: 8px 0 15px 0;
          .v-messages.theme--light:not(.error--text) {
            .v-messages__message {
              font-size: 12px;
              font-weight: normal;
              font-stretch: normal;
              font-style: normal;
              letter-spacing: normal;
              text-align: left;
              color: #989898;
            }
          }
        }
      }
    }
  }
  &_action {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    &-delete {
      padding: 9px 0;
    }
    padding: 15px 0;
    h3 {
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }
  }
  &_btn {
    &-submit {
      &.theme--light.v-btn.v-btn--has-bg {
        width: 158px;
        height: 36px;
        border-radius: 5px;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
        background-color: #0075c2;
        font-size: 14px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        text-align: left;
        color: #fff;
      }
      &.theme--light.v-btn.v-btn--disabled.v-btn--has-bg {
        background-color: #f3f3f3 !important;
        box-shadow: none;
        color: #ccc !important;
      }
    }

    &-delete {
      .icon-trash {
        font-size: 16px;
        color: #989898;
      }
    }
  }

  &_collapse {
    margin: 15px 0;
    cursor: pointer;
    display: inline-block;
    width: 100%;
    h3 {
      display: inline-block;
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }
    h4 {
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: left;
      color: #666;
      margin-top: 10px;
      span {
        margin-left: 10px;
        font-size: 12px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.67;
        letter-spacing: normal;
        text-align: left;
        color: #989898;
      }
    }
    h6 {
      font-size: 12px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: left;
      color: #989898;
      margin-top: 5px;
    }
    &--icon {
      margin-left: 5px;
      color: #c1c1c1 !important;
    }
  }
  &_dialog {
    position: relative;

    &-cancel {
      color: #989898 !important;
    }
    &-delete {
      color: #eb545b !important;
    }
    &-btn {
      width: 120px;
      font-size: 14px !important;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.43;
      letter-spacing: normal;
      text-align: center;
      border-radius: 5px;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
    }
    &-content {
      margin: 20px 0;
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.43;
      letter-spacing: normal;
      text-align: center;
      color: #666;
    }
  }
  &_selectedCams {
    margin-top: 10px;
    min-width: 400px;
    &--items {
      gap: 10px;
      width: 100%;
      display: flex;
      margin-bottom: 5px;
      span {
        color: #989898;
        font-size: 12px;
        line-height: 1.67;
        font-weight: normal;
      }
    }
    &--title {
      color: #666;
      width: 250px;
      font-size: 14px;
      font-weight: normal;
    }
  }
}
</style>
